import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import axios, { type AxiosError, type AxiosResponse } from 'axios';
import toast from 'react-hot-toast';
import i18n from '../i18n/config';
import { apiUrl } from '../config/api';
import { getApiErrorMessage, isAxiosNetworkError, shouldSuppressNetworkErrorToast, unwrapApiData } from '../utils/apiResponse';
import { initializePushNotifications, setupNotificationListeners } from '../utils/pushNotifications';
import { StorageManager } from '../utils/StorageManager';

axios.defaults.withCredentials = true;

axios.interceptors.response.use(
  (res: AxiosResponse) => res,
  (err: AxiosError) => {
    if (isAxiosNetworkError(err) && !shouldSuppressNetworkErrorToast(err.config)) {
      toast.error(getApiErrorMessage(err));
    }
    return Promise.reject(err);
  }
);

interface User {
  id: number;
  email: string;
  name: string;
  phone: string;
  location: string;
  latitude?: number;
  longitude?: number;
  city?: string;
  streetAddress?: string;
  isAdmin: boolean;
  isVerified: boolean;
  isPhoneVerified: boolean;
  profileImage?: string;
  termsAcceptedAt?: string | null;
  requiresTermsAcceptance?: boolean;
  sponsoredGrant?: number;
  sponsoredUsed?: number;
  sponsoredShowToggle?: boolean;
}

const TOKEN_STORAGE_KEY = 'token';

function getStoredToken(): string | null {
  return StorageManager.getItem(TOKEN_STORAGE_KEY);
}

function setStoredToken(token: string, rememberMe: boolean): void {
  StorageManager.setItem(TOKEN_STORAGE_KEY, token, rememberMe);
}

function clearStoredToken(): void {
  StorageManager.removeItem(TOKEN_STORAGE_KEY);
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loginWithPassword: (phone: string, password: string, rememberMe?: boolean) => Promise<User | null>;
  adminLogin: (email: string, password: string, adminPassword: string) => Promise<User | null>;
  registerWithPhone: (data: RegisterPhoneData) => Promise<RegisterResponse>;
  resetPasswordByPhone: (verificationToken: string, newPassword: string) => Promise<void>;
  updatePhoneWithVerification: (verificationToken: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  updateUser: (userData: Partial<User>) => Promise<void>;
  setUserFromData: (userData: Partial<User> | null) => void;
  acceptTerms: () => Promise<void>;
}

export interface RegisterPhoneData {
  phone: string;
  verificationToken?: string;
  name: string;
  location: string;
  password: string;
  city?: string;
  streetAddress?: string;
  latitude?: number;
  longitude?: number;
  termsAcceptedAt?: string;
}

interface RegisterResponse {
  userId?: string;
  requiresVerification: boolean;
  verificationMethod: string;
  verificationSent?: boolean;
  token?: string;
  user?: User;
  success?: boolean;
  message?: string;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => getStoredToken());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  useEffect(() => {
    const fetchMe = async () => {
      const response = await axios.get(apiUrl('/auth/me'), {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 20000,
        skipNetworkErrorToast: true
      });
      const me = unwrapApiData<{ user?: User }>(response.data);
      setUser(me.user ?? (response.data as { user?: User }).user ?? null);
    };

    const checkAuth = async () => {
      if (token) {
        try {
          await fetchMe();
        } catch (error) {
          console.error('Auth check failed:', error);
          const status = (error as { response?: { status?: number } }).response?.status;
          if (status === 401) {
            clearStoredToken();
            setToken(null);
            setUser(null);
          } else if (isAxiosNetworkError(error)) {
            try {
              await new Promise((r) => setTimeout(r, 1200));
              await fetchMe();
            } catch (retryErr) {
              const retryStatus = (retryErr as { response?: { status?: number } }).response?.status;
              if (retryStatus === 401) {
                clearStoredToken();
                setToken(null);
                setUser(null);
              }
            }
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const loginWithPassword = async (phone: string, password: string, rememberMe: boolean = true): Promise<User | null> => {
    try {
      const response = await axios.post(apiUrl('/auth/login-with-password'), { phone, password });
      const payload = unwrapApiData<{ token?: string; user?: User }>(response.data);
      const newToken = payload.token ?? (response.data as { token?: string }).token;
      const userData = payload.user ?? (response.data as { user?: User }).user;
      if (!newToken) throw new Error('No token in login response');
      setStoredToken(newToken, rememberMe);
      setToken(newToken);
      setUser(userData ?? null);
      try {
        setupNotificationListeners();
        await initializePushNotifications(newToken);
      } catch (err) {
        console.error('Push notifications init error:', err);
      }
      toast.success(i18n.t('toastAuth.loginOk'));
      return userData || null;
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error));
      throw error;
    }
  };

  const adminLogin = async (email: string, password: string, adminPassword: string): Promise<User | null> => {
    try {
      const response = await axios.post(apiUrl('/auth/admin-login'), { email, password, adminPassword });
      const payload = unwrapApiData<{ token?: string; user?: User }>(response.data);
      const newToken = payload.token ?? (response.data as { token?: string }).token;
      const userData = payload.user ?? (response.data as { user?: User }).user;
      if (!newToken) throw new Error('No token in admin login response');
      setStoredToken(newToken, true);
      setToken(newToken);
      setUser(userData ?? null);
      toast.success(i18n.t('toastAuth.adminLoginOk', 'تم تسجيل دخول المدير بنجاح'));
      return userData || null;
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error));
      throw error;
    }
  };

  const registerWithPhone = async (data: RegisterPhoneData): Promise<RegisterResponse> => {
    try {
      const payload: Record<string, unknown> = {
        phone: data.phone,
        name: data.name,
        location: data.location,
        password: data.password,
        city: data.city ?? null,
        streetAddress: data.streetAddress ?? null,
        latitude: data.latitude ?? null,
        longitude: data.longitude ?? null,
      };
      if (data.verificationToken) {
        payload.verificationToken = data.verificationToken;
      }
      if (data.termsAcceptedAt) {
        payload.termsAcceptedAt = data.termsAcceptedAt;
      }
      const response = await axios.post(apiUrl('/auth/register-phone'), payload);
      const regPayload = unwrapApiData<{ token?: string; user?: User }>(response.data);
      const regToken = regPayload.token ?? (response.data as { token?: string }).token;
      const regUser = regPayload.user ?? (response.data as { user?: User }).user;
      if (regToken) {
        setStoredToken(regToken, true);
        setToken(regToken);
        setUser(regUser ?? null);
        toast.success(i18n.t('toastAuth.registerAutoLogin'));
      } else {
        toast.success(i18n.t('toastAuth.registerOk'));
      }
      return response.data;
    } catch (error: any) {
      toast.error(getApiErrorMessage(error));
      throw error;
    }
  };

  const resetPasswordByPhone = async (verificationToken: string, newPassword: string) => {
    try {
      await axios.post(apiUrl('/auth/reset-password-by-phone'), { verificationToken, newPassword });
      toast.success(i18n.t('toastAuth.resetPasswordByPhoneOk'));
    } catch (error: any) {
      toast.error(getApiErrorMessage(error));
      throw error;
    }
  };

  const updatePhoneWithVerification = async (verificationToken: string) => {
    try {
      const response = await axios.post(apiUrl('/auth/update-phone'), { verificationToken }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = unwrapApiData<{ user?: User; message?: string }>(response.data);
      const nextUser = payload.user ?? (response.data as { user?: User }).user;
      if (nextUser) {
        setUser(prev => prev ? { ...prev, ...nextUser } : nextUser);
      }
      toast.success(
        (payload.message ?? (response.data as { message?: string }).message) || i18n.t('toastAuth.updatePhoneOk')
      );
    } catch (error: any) {
      toast.error(getApiErrorMessage(error));
      throw error;
    }
  };

  const logout = () => {
    clearStoredToken();
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    toast.success(i18n.t('toastAuth.logoutOk'));
  };

  const updateUser = async (userData: Partial<User>) => {
    if (!user?.id) { toast.error(i18n.t('toastAuth.sessionExpired')); return; }
    try {
      const response = await axios.put(apiUrl(`/users/${user.id}`), userData);
      const uPayload = unwrapApiData<{ user?: User }>(response.data);
      setUser(uPayload.user ?? (response.data as { user?: User }).user ?? null);
      toast.success(i18n.t('toastAuth.profileUpdateOk'));
    } catch (error: any) {
      toast.error(getApiErrorMessage(error));
      throw error;
    }
  };

  const setUserFromData = (userData: Partial<User> | null) => {
    if (!userData) {
      setUser(null);
      return;
    }
    setUser(prev => {
      if (!prev) return userData as User;
      const next = { ...prev, ...userData };
      if ('profileImage' in userData) next.profileImage = userData.profileImage;
      return next;
    });
  };

  const acceptTerms = async () => {
    if (!token) return;
    try {
      const response = await axios.post(apiUrl('/auth/accept-terms'), {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = unwrapApiData<{ user?: User }>(response.data);
      const u = payload.user ?? (response.data as { user?: User }).user;
      if (u) setUser(u);
      else setUser(prev => prev ? { ...prev, termsAcceptedAt: new Date().toISOString() } : null);
      toast.success(i18n.t('toastAuth.acceptTermsOk'));
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error));
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    loginWithPassword,
    adminLogin,
    registerWithPhone,
    resetPasswordByPhone,
    updatePhoneWithVerification,
    logout,
    loading,
    updateUser,
    setUserFromData,
    acceptTerms,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
